import React, { useEffect, useState } from "react";
import { Loader2, BarChart3, IndianRupee, ClipboardList, Users, TrendingUp, Download } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { KpiCard } from "@/components/common";
import { api, inr, API, TOKEN_KEY } from "@/lib/api";

const STATUS_COLORS = {
  placed: "#94a3b8", confirmed: "#3b82f6", preparing: "#f59e0b", ready: "#8b5cf6",
  picked_up: "#0ea5e9", on_the_way: "#0891b2", delivered: "#10b981", cancelled: "#ef4444",
};

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-coral" /></div>;
  if (!data) return <div className="py-16 text-center text-ink-600">Analytics unavailable.</div>;

  const statusRows = Object.entries(data.status_dist || {}).sort((a, b) => b[1] - a[1]);
  const payRows = Object.entries(data.payment_split || {});
  const totalOrders = data.total_orders || 1;

  return (
    <div className="space-y-4" data-testid="admin-analytics">
      <div className="flex items-center gap-2">
        <BarChart3 className="text-coral" size={22} />
        <h2 className="font-display font-bold text-lg">Analytics Dashboard</h2>
        <button onClick={() => window.open(`${API}/admin/analytics/export?auth=${localStorage.getItem(TOKEN_KEY)}`, "_blank")} className="ml-auto bg-coral text-white text-sm font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5 tap" data-testid="analytics-export-btn">
          <Download size={15} /> Download CSV
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={ClipboardList} label="Total Orders" value={data.total_orders} />
        <KpiCard icon={IndianRupee} label="Revenue (delivered)" value={inr(data.revenue)} tone="gold" />
        <KpiCard icon={TrendingUp} label="Avg Order Value" value={inr(data.aov)} tone="veg" />
        <KpiCard icon={ClipboardList} label="Cancel Rate" value={`${data.cancel_rate}%`} />
        <KpiCard icon={Users} label="Customers" value={data.customers} />
        <KpiCard icon={ClipboardList} label="Delivered" value={data.delivered_orders} tone="veg" />
        <KpiCard icon={Users} label="Restaurants" value={data.restaurants} />
        <KpiCard icon={Users} label="Delivery Partners" value={data.delivery_partners} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-border p-4">
          <h3 className="font-bold text-ink mb-3">Revenue trend (last 30 days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v, n) => (n === "revenue" ? inr(v) : v)} />
              <Line type="monotone" dataKey="revenue" stroke="#FF6B35" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-border p-4">
          <h3 className="font-bold text-ink mb-3">Orders per day</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="orders" fill="#FF6B35" radius={[4, 4, 0, 0]} />
              <Bar dataKey="delivered" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-border p-4">
          <h3 className="font-bold text-ink mb-3">New customers (last 30 days)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.new_customers}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-border p-4">
          <h3 className="font-bold text-ink mb-3">Order status distribution</h3>
          <div className="space-y-2">
            {statusRows.map(([st, n]) => (
              <div key={st} data-testid={`status-dist-${st}`}>
                <div className="flex justify-between text-xs mb-0.5"><span className="capitalize text-ink-600">{st.replace("_", " ")}</span><span className="font-bold text-ink">{n} ({Math.round((n / totalOrders) * 100)}%)</span></div>
                <div className="h-2 rounded-full bg-surface overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(n / totalOrders) * 100}%`, background: STATUS_COLORS[st] || "#FF6B35" }} /></div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-border">
            <h4 className="text-xs font-bold text-ink-600 uppercase mb-2">Payment split</h4>
            <div className="flex flex-wrap gap-2">
              {payRows.map(([pm, n]) => <span key={pm} className="text-xs font-semibold bg-surface px-2.5 py-1 rounded-full text-ink">{pm.toUpperCase()}: {n}</span>)}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-border p-4">
        <h3 className="font-bold text-ink mb-3">Top restaurants by revenue</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-ink-600 border-b border-border">{["#", "Restaurant", "Orders", "Revenue"].map((c) => <th key={c} className="py-2 font-semibold">{c}</th>)}</tr></thead>
            <tbody>
              {data.top_restaurants.map((r, i) => (
                <tr key={r.name} className="border-b border-border last:border-0" data-testid={`top-rest-${i}`}>
                  <td className="py-2 text-ink-600">{i + 1}</td>
                  <td className="py-2 font-semibold text-ink">{r.name}</td>
                  <td className="py-2">{r.orders}</td>
                  <td className="py-2 font-bold text-coral">{inr(r.revenue)}</td>
                </tr>
              ))}
              {!data.top_restaurants.length && <tr><td colSpan={4} className="py-6 text-center text-ink-600">No delivered orders yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
